import React, { useMemo, useState } from "react";
import Tabs, { tabsPropsType } from "@/components/controls/Tabs";
import Dropdown, {
  dropdownPropsType
} from "@/components/controls/Dropdown/Dropdown";
import PipelineFunnelChart from "@/components/graphs/PipelineFunnelChart/PipelineFunnelChart";
import { formatNumber } from "@/utils/numbers";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import MRRTooltip from "@/components/controls/MRRTooltip";
import { UserData } from "@/entities/users/types";
import WidgetCard from "@/components/controls/WidgetCard";
import { useTranslation } from "react-i18next";
import { Info } from "@dreamhub/designsystem/icons";
import IconContainer from "@/components/ui/icon-container";
import Text from "@/components/ui/text";
import {
  ContributorValues,
  PeerComparisonData,
  PipelineFunnelData,
  SettingsPeriod,
  StageData
} from "@/entities/reporting/types";
import {
  periodEnumToString,
  periodStringToEnum
} from "@/entities/reporting/helpers";
import { useArrMrr } from "@/services/context/ArrMrrContext";
import ComparisonChart from "@/components/graphs/ComparisonChart/ComparisonChart";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { fromPairs, map, sumBy } from "lodash";

interface Props {
  pipelineFunnelChartProps: PipelineFunnelData;
  comparisonChartData: PeerComparisonData;
  userData?: UserData;
  periodSelect: (period: SettingsPeriod) => void;
  isLoading?: boolean;
  isAE: boolean;
}

const SalesPipeline: React.FC<Props> = ({
  pipelineFunnelChartProps,
  comparisonChartData,
  userData,
  periodSelect,
  isLoading,
  isAE
}) => {
  const { t } = useTranslation();
  const showIndustryValue = useFeatureFlag(FeatureFlags.INDUSTRY_VALUE).enabled;
  const { isArr, label: arrMrrLabel } = useArrMrr();
  const { stages = [], period = SettingsPeriod.ALL_TIMES } =
    pipelineFunnelChartProps ?? {};

  const tabsProps: tabsPropsType = {
    options: {
      mrrArr: arrMrrLabel,
      mrrArrProbability: `${arrMrrLabel}${t("salesPipeline.timeProbability")}`
    },
    defaultValue: "mrrArr"
  };
  const [selectedTab, setSelectedTab] = useState(tabsProps.defaultValue);
  const isProbabilityMode = useMemo(
    () => selectedTab === "mrrArrProbability",
    [selectedTab]
  );

  const mergedComparisonChartProps = useMemo(() => {
    if (!comparisonChartData) return null;

    return {
      datasets: [
        {
          name: t(`comparison.titles.you`),
          value: isProbabilityMode
            ? comparisonChartData.you.weightedValue
            : comparisonChartData.you.value
        },
        {
          name: t(`comparison.titles.team`),
          value: isProbabilityMode
            ? comparisonChartData.team.weightedValue
            : comparisonChartData.team.value
        },
        {
          name: t(`comparison.titles.organization`),
          value: isProbabilityMode
            ? comparisonChartData.org.weightedValue
            : comparisonChartData.org.value
        },
        {
          name: t(`comparison.titles.industry`),
          value: comparisonChartData.industry
            ? isProbabilityMode
              ? comparisonChartData.industry.weightedValue
              : comparisonChartData.industry.value
            : 0
        }
      ],
      userData
    };
  }, [comparisonChartData, userData, isProbabilityMode]);

  const periodsDropdownProps: dropdownPropsType = {
    label: t("salesPipeline.periodsLabel"),
    options: fromPairs(
      map(["month", "quarter", "year", "allTimes"], (timeframe) => [
        timeframe,
        t(`timeframes.${timeframe}`)
      ])
    ),
    defaultValue: periodEnumToString(period)
  };

  const totalsData: StageData[] = useMemo(() => {
    if (!stages?.length || !stages?.[0]?.contributors) return [];
    const firstStageContributors = stages?.[0].contributors;

    return [
      {
        stageId: -1,
        stageOrder: 0,
        stageName: "Totals",
        contributors: Object.keys(firstStageContributors).reduce(
          (acc, key) => {
            acc[key] = {
              ...firstStageContributors[key],
              value: stages.reduce(
                (sum, stage) => sum + (stage.contributors[key]?.value ?? 0),
                0
              ),
              weightedValue: stages.reduce(
                (sum, stage) =>
                  sum + (stage.contributors[key]?.weightedValue ?? 0),
                0
              )
            };
            return acc;
          },
          {} as Record<string, ContributorValues>
        ),
        totalValue: sumBy(stages, "totalValue") ?? 0,
        totalWeightedValue: sumBy(stages, "totalWeightedValue") ?? 0
      }
    ];
  }, [stages]);

  const formattedTotal = useMemo(() => {
    const total = totalsData[0]
      ? isProbabilityMode
        ? (totalsData[0].totalWeightedValue ?? 0)
        : (totalsData[0].totalValue ?? 0)
      : 0;
    return formatNumber(total, 0, true, false);
  }, [totalsData, isProbabilityMode]);

  return (
    <WidgetCard
      cardHeight="35rem"
      title={t("salesPipeline.title")}
      isLoading={isLoading}
      infoTooltip={t("salesPipeline.info")}
      feedBackButtonIdContext="sales-pipeline"
      tabs={
        <Tabs {...tabsProps} valueSelected={(value) => setSelectedTab(value)} />
      }
      filter={
        <Dropdown
          {...periodsDropdownProps}
          valueSelected={(value) => periodSelect(periodStringToEnum(value))}
        />
      }
    >
      <div className="flex w-full gap-x-10">
        <div
          className={`transition-opacity duration-300 w-[calc(100%-11.5rem)] ${pipelineFunnelChartProps ? "opacity-100" : "opacity-0"}`}
        >
          <PipelineFunnelChart
            stagesData={stages}
            isArr={isArr}
            isProbabilityMode={isProbabilityMode}
            isAE={isAE}
          />
        </div>
        <div
          className={`transition-opacity duration-300 flex flex-col gap-6 py-1 h-full justify-between w-[12.5rem] ${pipelineFunnelChartProps ? "opacity-100" : "opacity-0"}`}
        >
          <Text as="div" variant="lgLabel" className="h-40 flex flex-col gap-3">
            <div className="flex gap-3 justify-start text-h3">
              <Text variant="bodySecondary">
                {t("salesPipeline.totalArrMrr.titleTotal")} {arrMrrLabel}
              </Text>
              <Tooltip data-test="sales-pipeline-info-tooltip">
                <TooltipTrigger className="text-lg text-tertiary self-start">
                  <IconContainer nativeStroke className="grow items-start">
                    <Info data-test="sales-pipeline-info-icon" />
                  </IconContainer>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    {t("salesPipeline.totalArrMrr.tooltip", {
                      arrMrr: arrMrrLabel
                    })}
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>
            <div className="w-full">
              <MRRTooltip
                trigger={
                  <Text variant="title1H1" className="w-full">
                    <span className="mr-[2px]">
                      {formattedTotal.currencySign}
                    </span>
                    {formattedTotal.value}
                  </Text>
                }
                index={0}
                stagesData={totalsData}
                isArr={isArr}
                isProbabilityMode={isProbabilityMode}
              />
            </div>
          </Text>
          {mergedComparisonChartProps && (
            <div className="flex flex-col justify-end h-96 max-w-40 pr-4">
              <Text
                as="div"
                variant="bodySecondary"
                className="text-start mb-2"
              >
                {t("comparison.title")}
              </Text>
              <ComparisonChart
                {...mergedComparisonChartProps}
                hideIndustry={!showIndustryValue}
              />
            </div>
          )}
        </div>
      </div>
    </WidgetCard>
  );
};

export default SalesPipeline;
